
import { namespace } from 'vuex-class';
import { Component, Mixins, Prop } from 'vue-property-decorator';

import { ObjectType, Offer, Offer as OfferGraphql, SraEventAction } from '@/@types/graphql-types';
import BuyBoxRow from '@/components/buybox/BuyBoxRow.vue';
import {
	GetTitleOffersPromotedDocument,
	GetTitleOffersPromotedQueryVariables,
} from '@/components/buybox/graphql/queries/GetTitleOffersPromoted.query';

import {
	NarrativeObjectType,
	OfferLocation,
	getBuyboxCategoryByMonetizationGraphql,
	trackClickOutGraphql,
} from '@/helpers/offer-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { ImpressionTrackingEvents } from '@/enums/events';
import { BuyboxOfferCategory } from '@/interfaces/buybox';

import type { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { toastError } from '@/helpers/toast/toast-helper';
import BackendTrackingMixin from '@/mixins/BackendTracking.vue';
import type { SponsoredAdFragmentFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';

type TitleOffersPromotedData = {
	promotedOffers: TitleOfferFragment[];
	promotedBundles: {
		node: {
			clearName: string;
			icon: string;
			technicalName: string;
			bundleId: number;
		};
		promotionUrl: string;
		offer: TitleOfferFragment;
	}[];
};

type BuyboxRowOffer = TitleOfferFragment & {
	iconUrl?: string;
	bundleId?: number;
	bundleTechnicalName?: string;
};

type OfferCategories =
	| BuyboxOfferCategory.STREAM
	| BuyboxOfferCategory.RENT
	| BuyboxOfferCategory.BUY
	| BuyboxOfferCategory.BUNDLE;

const Language = namespace('language');

const CategoryOrder = [
	BuyboxOfferCategory.STREAM,
	BuyboxOfferCategory.RENT,
	BuyboxOfferCategory.BUY,
	BuyboxOfferCategory.BUNDLE,
];

@Component({
	name: 'PromotedBuyBox',
	components: { BuyBoxRow },
	apollo: {
		titleOffersPromotedData: {
			query: GetTitleOffersPromotedDocument,
			variables(): GetTitleOffersPromotedQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data.node;
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Promoted Buy Box Data]:',
					{ error: error, where: 'Component: PromotedBuyBox' },
					'error'
				);
				process.client ? this.showErrorToast() : null;
			},
		},
	},
})
export default class PromotedBuyBox extends Mixins(BackendTrackingMixin) {
	titleOffersPromotedData: TitleOffersPromotedData = {} as TitleOffersPromotedData;

	@Prop({ required: true }) titleId: string;
	@Prop({ required: true }) titleObjectType: NarrativeObjectType; // base title objectType
	@Prop({ required: true }) titleObjectId: number; // base title id
	@Prop({ default: null }) sponsoredAd: SponsoredAdFragmentFragment;

	// You need to pass this prop when title is Season/Episode, because BuyBox component has functionality to set
	// title into Watchlist, but this mutation available only for Movie/Show. So basically when you set Season/Episode
	// to Watchlist we set whole Show to watchlist.
	@Prop({ default: null }) showDetails: {
		id: string;
		objectId: number;
		objectType: ObjectType;
		title: string;
	};
	@Prop({ default: () => null }) seasonNumber: number | null;
	@Prop({ default: () => null }) episodeNumber: number | null;
	@Prop({ default: false }) onlyBundle: boolean;
	@Prop({ default: false }) isMiddleBuyBox: boolean;
	@Prop({ default: () => [] }) additionalContexts: (SnowplowModuleContext | SnowplowContext)[];

	@Language.Getter declare language: string;
	@Language.Getter declare country: string;

	get title() {
		return {
			id: this.titleId,
			objectId: this.titleObjectId,
			objectType: this.titleObjectType,
		};
	}

	get queryVariables(): GetTitleOffersPromotedQueryVariables {
		return {
			nodeId: this.titleId,
			country: this.country,
			language: this.language,
		};
	}

	get promotedBundles() {
		return this.titleOffersPromotedData.promotedBundles || [];
	}

	get promotedOffers() {
		return (this.titleOffersPromotedData.promotedOffers as TitleOfferFragment[]) || [];
	}

	get hasPromotedOffers() {
		return this.promotedOffers?.length > 0;
	}

	get hasPromotedBundles() {
		return this.promotedBundles?.length > 0;
	}

	get categoryTranslatables() {
		return {
			[BuyboxOfferCategory.STREAM]: this.$t('WEBAPP_STREAM'),
			[BuyboxOfferCategory.RENT]: this.$t('WEBAPP_RENT'),
			[BuyboxOfferCategory.BUY]: this.$t('WEBAPP_BUY'),
			[BuyboxOfferCategory.BUNDLE]: this.$t('WEBAPP_BUNDLES'),
		};
	}

	get categories(): OfferCategories[] {
		if (this.onlyBundle) {
			return [BuyboxOfferCategory.BUNDLE];
		}

		const updatedOffers = (this.promotedOffers ?? [])
			.map(offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType))
			.filter(key => key !== undefined)
			.filter((item, index, offers) => offers.indexOf(item) === index) as OfferCategories[];

		if (this.hasPromotedBundles) {
			updatedOffers.push(BuyboxOfferCategory.BUNDLE);
		}

		updatedOffers.sort((a, b) => CategoryOrder.indexOf(a) - CategoryOrder.indexOf(b));

		return updatedOffers;
	}

	get sponsoredRecommendationCohort() {
		return this.sponsoredAd?.holdoutGroup ? '0' : '1';
	}

	showErrorToast() {
		toastError({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}

	getOffersByCategory(category: BuyboxOfferCategory): BuyboxRowOffer[] {
		if (category === BuyboxOfferCategory.BUNDLE) {
			return this.promotedBundles.map(promotedBundle => ({
				...promotedBundle.offer,
				standardWebURL: promotedBundle.promotionUrl,
				iconUrl: promotedBundle.node?.icon,
				bundleId: promotedBundle.node?.bundleId,
				bundleTechnicalName: promotedBundle.node?.technicalName,
			}));
		}

		return this.promotedOffers.filter(
			offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType) === category
		);
	}

	clickOut(payload: { buyboxOffer: OfferGraphql; isBundle: boolean; evt: MouseEvent }) {
		const { buyboxOffer: offer, isBundle } = payload;
		const contexts = [...this.additionalContexts];

		if (isBundle) {
			const bundleWithOffer = this.promotedBundles.find(promotedBundle => promotedBundle.offer.id === offer.id);

			if (bundleWithOffer) {
				const packagesContext = TrackingHelper.getPackageContext(
					offer.package.packageId,
					offer.package.technicalName,
					'bundle',
					bundleWithOffer.node.bundleId,
					bundleWithOffer.node.technicalName
				);
				contexts.push(packagesContext);
			}
		}

		if (this.sponsoredAd?.campaign.node.id === this.titleId) {
			const { packageId } = this.sponsoredAd?.campaign.watchNowOffer.package;
			if (payload.buyboxOffer.package.packageId === packageId) {
				const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer as Offer);
				const contexts = (this.additionalContexts || []).concat([
					new SnowplowTitleContextGraphql(this.titleObjectId, this.titleObjectType),
				]);
				contexts.push(clickoutContext);

				TrackingHelper.trackEvent(
					ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
					{
						action: 'buybox_clickout',
						label: `${this.titleId}_${packageId}`,
						property: 'buybox',
						value: this.sponsoredRecommendationCohort,
					},
					contexts
				);

				this.$backendTracking.trackSraEvent(SraEventAction.BuyboxClickout, this.sponsoredAd);
			}
		}

		trackClickOutGraphql({
			offer,
			objectType: this.showDetails?.objectType ?? this.titleObjectType,
			objectId: this.showDetails?.objectId ?? this.titleObjectId,
			seasonNumber: this.seasonNumber,
			episodeNumber: this.episodeNumber,
			offerLocation: this.isMiddleBuyBox ? OfferLocation.MIDDLE_BUYBOX : OfferLocation.PROMOTED_BUYBOX,
			additionalContexts: contexts,
		});
	}
}
